import type React from 'react';
import { Button } from 'src/components/Button';
import { InputFile } from 'src/components/InputFile';
import { InputText } from 'src/components/InputText';
import { Select } from 'src/components/Select';
import type { NameFiles } from 'src/features/createAccount/screens/CreateAccount/ui';
import {
  FormInputGradeSize,
  FormRegisterProduct,
} from 'src/features/createProduct/types/types';
import type { UseFormReturn } from 'src/hooks';
import type { Category } from 'src/model/Category';
import TextArea from 'src/components/TextArea';
import type { Grade } from 'src/model/Grade';
import InputTags from '../components/InputsTags';
import CategorySelector from './../components/CategorySelect';
import type { GradeSize } from 'src/model/GradeSize';
import { Checkbox } from 'src/components/Checkbox';

import {
  type FormEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'bottom' | 'left' | 'right';

const Tooltip: React.FC<{
  text: string;
  children: React.ReactNode;
  placement?: Placement;
  delay?: number;
  maxWidth?: number;
}> = ({ text, children, placement = 'top', delay = 100, maxWidth = 280 }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [side, setSide] = useState<Placement>(placement);
  const [arrow, setArrow] = useState({ x: 0, y: 0 });
  const tRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | null>(null);

  const show = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), delay);
  };

  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setOpen(false);
  };

  const place = () => {
    const a = anchorRef.current;
    const p = tRef.current;
    if (!a || !p) return;

    const ar = a.getBoundingClientRect();
    p.style.visibility = 'hidden';
    p.style.display = 'block';
    const pr = p.getBoundingClientRect();

    const o = 10;
    const pad = 8;

    const fitsTop = ar.top - pr.height - o >= pad;
    const fitsBottom = ar.bottom + pr.height + o <= window.innerHeight - pad;
    const fitsLeft = ar.left - pr.width - o >= pad;
    const fitsRight = ar.right + pr.width + o <= window.innerWidth - pad;

    let s: Placement = placement;
    if (s === 'top' && !fitsTop)
      s = fitsBottom ? 'bottom' : fitsRight ? 'right' : 'left';
    if (s === 'bottom' && !fitsBottom)
      s = fitsTop ? 'top' : fitsRight ? 'right' : 'left';
    if (s === 'left' && !fitsLeft)
      s = fitsRight ? 'right' : fitsTop ? 'top' : 'bottom';
    if (s === 'right' && !fitsRight)
      s = fitsLeft ? 'left' : fitsTop ? 'top' : 'bottom';

    let x = 0;
    let y = 0;

    if (s === 'top') {
      x = ar.left + ar.width / 2 - pr.width / 2;
      y = ar.top - pr.height - o;
    } else if (s === 'bottom') {
      x = ar.left + ar.width / 2 - pr.width / 2;
      y = ar.bottom + o;
    } else if (s === 'left') {
      x = ar.left - pr.width - o;
      y = ar.top + ar.height / 2 - pr.height / 2;
    } else {
      x = ar.right + o;
      y = ar.top + ar.height / 2 - pr.height / 2;
    }

    if (x < pad) x = pad;
    if (x + pr.width > window.innerWidth - pad)
      x = window.innerWidth - pr.width - pad;
    if (y < pad) y = pad;
    if (y + pr.height > window.innerHeight - pad)
      y = window.innerHeight - pr.height - pad;

    let ax = ar.left + ar.width / 2 - 4;
    let ay = ar.top + ar.height / 2 - 4;
    if (s === 'top') {
      ay = y + pr.height - 1;
      ax = Math.min(
        Math.max(ar.left + ar.width / 2 - 4, x + 10),
        x + pr.width - 10
      );
    } else if (s === 'bottom') {
      ay = y - 7;
      ax = Math.min(
        Math.max(ar.left + ar.width / 2 - 4, x + 10),
        x + pr.width - 10
      );
    } else if (s === 'left') {
      ax = x + pr.width - 1;
      ay = Math.min(
        Math.max(ar.top + ar.height / 2 - 4, y + 10),
        y + pr.height - 10
      );
    } else {
      ax = x - 7;
      ay = Math.min(
        Math.max(ar.top + ar.height / 2 - 4, y + 10),
        y + pr.height - 10
      );
    }

    setSide(s);
    setPos({ x, y });
    setArrow({ x: ax, y: ay });
    p.style.visibility = 'visible';
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, placement, text]);

  useEffect(() => {
    const onWinChange = () => open && place();
    window.addEventListener('resize', onWinChange);
    window.addEventListener('scroll', onWinChange, true);
    return () => {
      window.removeEventListener('resize', onWinChange);
      window.removeEventListener('scroll', onWinChange, true);
    };
  }, [open]);

  return (
    <span
      ref={anchorRef}
      className="inline-flex align-middle"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby="tooltip"
    >
      {children}
      {open &&
        createPortal(
          <>
            <div
              ref={tRef}
              id="tooltip"
              role="tooltip"
              data-side={side}
              className="fixed z-50 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white shadow-lg opacity-100 will-change-transform"
              style={{ top: pos.y, left: pos.x, maxWidth }}
            >
              <div className="break-words">{text}</div>
            </div>
            <div
              className="fixed z-50 h-2 w-2 rotate-45 bg-gray-900"
              style={{ left: arrow.x, top: arrow.y }}
            />
          </>,
          document.body
        )}
    </span>
  );
};

const clamp = (s: string, n: number) => {
  const t = (s ?? '')
    .toString()
    .replace(',', '.') // converte vírgula para ponto
    .replace(/[^0-9.]/g, ''); // remove tudo que não é número ou ponto

  const i = t.indexOf('.');
  const result =
    i === -1 ? t : t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, '');
  return result.slice(0, n);
};
const onlyDigits = (s: string) => (s ?? '').toString().replace(/\D+/g, '');
const dec = (s: string) => {
  const t = (s ?? '')
    .toString()
    .replace(',', '.')
    .replace(/[^0-9.]/g, '');
  const i = t.indexOf('.');
  return i === -1 ? t : t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, '');
};

const AdvancedGradeForm: React.FC<{
  onGradeChange: (grades: any[]) => void;
}> = ({ onGradeChange }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [attributes, setAttributes] = useState<Record<string, string[]>>({});
  const [variants, setVariants] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [colorFiles, setColorFiles] = useState<Record<string, File[]>>({});
  const [colorPreviews, setColorPreviews] = useState<Record<string, string[]>>(
    {}
  );
  const [fileErrors, setFileErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [variantFiles, setVariantFiles] = useState<Record<number, File[]>>({});
  const [variantPreviews, setVariantPreviews] = useState<
    Record<number, string[]>
  >({});

  const categoryTemplates = {
    clothing: ['Cor', 'Tamanho'],
    electronics: ['Cor', 'Voltagem'],
    generic: ['Cor', 'Opção Extra'],
  };

  const addAttribute = (key: string, value: string) => {
    if (!value.trim()) return;
    const v = value.trim();
    setAttributes((prev) => {
      const next = { ...prev };
      if (!next[key]) next[key] = [];
      if (!next[key].includes(v)) next[key] = [...next[key], v];
      return next;
    });
  };

  const getColorKey = () =>
    headers.find((h) => h.toLowerCase().includes('cor')) || headers[0];

  const syncVariantsWithColorMedia = (
    base: any[],
    colorFilesMap = colorFiles,
    colorPreviewsMap = colorPreviews
  ) => {
    const ck = getColorKey();
    if (!ck) return base;
    return base.map((variant, i) => {
      const color = variant[ck];
      const cFiles = colorFilesMap[color] || [];
      const cPrev = colorPreviewsMap[color] || [];
      const vFiles = variantFiles[i] || [];
      const vPrev = variantPreviews[i] || [];
      return {
        ...variant,
        media: vFiles.length ? vFiles : cFiles,
        mediaPreview: vPrev.length ? vPrev : cPrev,
      };
    });
  };

  const removeAttribute = (key: string, value: string) => {
    setAttributes((prev) => {
      const next = { ...prev };
      next[key] = next[key].filter((v) => v !== value);
      return next;
    });
  };

  const cartesianProduct = (arrays: string[][]): string[][] =>
    arrays.reduce<string[][]>(
      (acc, curr) => acc.flatMap((a) => curr.map((b) => [...a, b])),
      [[]]
    );

  const generateVariants = () => {
    const entries = Object.entries(attributes).filter(
      ([, vals]) => vals.length
    );
    if (!entries.length) {
      alert('Adicione atributos.');
      return;
    }
    const newHeaders = entries.map(([k]) => k);
    setHeaders(newHeaders);
    const values = entries.map(([, vals]) => vals);
    const combos = cartesianProduct(values);
    const newVariants = combos.map((combo) => {
      const obj = Object.fromEntries(newHeaders.map((h, i) => [h, combo[i]]));
      return { ...obj, price: '', promo: 'Não', discount: '', stock: '' };
    });
    setVariants(newVariants);
    setColorFiles({});
    setColorPreviews({});
    setFileErrors({});
    setVariantFiles({});
    setVariantPreviews({});
  };

  const updateVariant = (index: number, field: string, value: string) => {
    const updated = [...variants];
    updated[index][field] = value;
    setVariants(updated);

    const next = syncVariantsWithColorMedia(updated);
    onGradeChange(next);
  };

  const bulkUpdate = (field: string) => {
    const value = prompt(`${field} para todas:`);
    if (value === null) return;
    const nextLocal = variants.map((v) => ({ ...v, [field]: value }));
    setVariants(nextLocal);
    const next = syncVariantsWithColorMedia(nextLocal);
    onGradeChange(next);
  };

  const clearTable = () => {
    if (!confirm('Tem certeza que deseja limpar toda a grade?')) return;
    setVariants([]);
    setHeaders([]);
    setVariantFiles({});
    setVariantPreviews({});
    setFileErrors({});
    onGradeChange([]);
  };

  const isAllowedType = (t: string) =>
    t.startsWith('image/jpeg') ||
    t.startsWith('image/png') ||
    t.startsWith('image/jpg') ||
    t.startsWith('video/mp4') ||
    t.startsWith('video/webm');

  const handleFilesChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (
      files.some(
        (f) => !(f.type.startsWith('image/') || f.type.startsWith('video/'))
      )
    ) {
      setFileErrors((prev) => ({ ...prev, [index]: 'Arquivos inválidos' }));
      return;
    }

    const limited = files.slice(0, 5);
    const readers = limited.map(
      (f) =>
        new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result || ''));
          r.onerror = reject;
          r.readAsDataURL(f);
        })
    );

    Promise.all(readers)
      .then((previews) => {
        setVariantFiles((prev) => ({ ...prev, [index]: limited }));
        setVariantPreviews((prev) => {
          const nextPrev = { ...prev, [index]: previews };
          // aplica prioridade: mídia da variante > mídia da cor
          const nextLocal = variants.map((v, i) => ({
            ...v,
            media: i === index ? limited : variantFiles[i] || [],
            mediaPreview: i === index ? previews : variantPreviews[i] || [],
          }));
          setVariants(nextLocal);
          const next = syncVariantsWithColorMedia(nextLocal);
          onGradeChange(next);
          return nextPrev;
        });
        setFileErrors((prev) => ({ ...prev, [index]: undefined }));
      })
      .catch(() =>
        setFileErrors((prev) => ({ ...prev, [index]: 'Falha ao ler arquivos' }))
      );
  };

  const handleColorFilesChange = (
    color: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = files.filter(
      (file) => file.type.startsWith('image/') || file.type.startsWith('video/')
    );
    if (validFiles.length !== files.length) {
      setFileErrors((prev) => ({
        ...prev,
        [color]: 'Apenas imagens e vídeos são permitidos.',
      }));
      return;
    }
    if (validFiles.length > 5) {
      setFileErrors((prev) => ({
        ...prev,
        [color]: 'Máximo de 5 arquivos por cor.',
      }));
      return;
    }

    const readers = validFiles.map(
      (f) =>
        new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result || ''));
          r.onerror = reject;
          r.readAsDataURL(f);
        })
    );

    Promise.all(readers)
      .then((previews) => {
        setColorFiles((prev) => ({ ...prev, [color]: validFiles }));
        setColorPreviews((prev) => {
          const nextPrevs = { ...prev, [color]: previews };
          // sincroniza imediatamente todas as variantes com a cor
          const next = syncVariantsWithColorMedia(
            variants,
            { ...colorFiles, [color]: validFiles },
            nextPrevs
          );
          onGradeChange(next);
          return nextPrevs;
        });
        setFileErrors((prev) => ({ ...prev, [color]: undefined }));
      })
      .catch(() =>
        setFileErrors((prev) => ({ ...prev, [color]: 'Falha ao ler arquivos' }))
      );
  };

  const getUniqueColors = () => {
    const ck = getColorKey();
    if (!ck) return [];
    const colors = new Set(variants.map((v) => v[ck]).filter(Boolean));
    return Array.from(colors) as string[];
  };

  return (
    <div className="space-y-6 p-4 border rounded-lg bg-gray-50">
      <div>
        <h3 className="text-lg font-semibold mb-4">
          Configuração Avançada de Grades
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categoria
              <Tooltip text="Selecione a categoria do produto para definir os atributos disponíveis">
                <span className="ml-1 text-blue-500 cursor-help">?</span>
              </Tooltip>
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                const template =
                  categoryTemplates[
                    e.target.value as keyof typeof categoryTemplates
                  ];
                if (template) {
                  const next: Record<string, string[]> = {};
                  template.forEach((attr) => (next[attr] = []));
                  setAttributes(next);
                }
              }}
              className="w-full rounded-lg p-2 bg-[#fafafa] shadow-inner border border-gray-300"
            >
              <option value="">Selecione…</option>
              <option value="clothing">Roupas</option>
              <option value="electronics">Eletrônicos</option>
              <option value="generic">Genérico / Outros</option>
            </select>
          </div>
        </div>

        {selectedCategory && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium mb-3">Atributos</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(attributes).map((key) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {key}
                    <Tooltip
                      text={`Adicione valores para ${key.toLowerCase()}`}
                    >
                      <span className="ml-1 text-blue-500 cursor-help">?</span>
                    </Tooltip>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {attributes[key].map((value) => (
                      <span
                        key={value}
                        className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                      >
                        {value}
                        <button
                          type="button"
                          onClick={() => removeAttribute(key, value)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    maxLength={10}
                    placeholder={`Ex.: ${key === 'Cor' ? 'Preto, Azul' : key === 'Tamanho' ? 'P, M, G' : '110v, 220vg'}`}
                    className="w-full rounded-lg p-2 bg-white border border-gray-300"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAttribute(key, e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                    onBlur={(e) => {
                      addAttribute(key, e.currentTarget.value);
                      e.currentTarget.value = '';
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-600">
            Preencha os atributos e clique em <strong>Gerar combinações</strong>
            .
          </p>
          <button
            type="button"
            onClick={generateVariants}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium"
          >
            Gerar combinações
          </button>
        </div>

        {variants.length > 0 && getUniqueColors().length > 0 && (
          <div className="space-y-4">
            <h4 className="text-md font-medium">Upload de Imagens por Cor</h4>
            <p className="text-sm text-gray-600">
              Faça upload de uma imagem por cor. Ela será aplicada a todos os
              tamanhos dessa cor.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getUniqueColors().map((color) => {
                const inputId = `colorFiles-${color}`;
                const previews = colorPreviews[color] || [];
                const error = fileErrors[color];

                return (
                  <div
                    key={color}
                    className="rounded-xl border bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <h5 className="font-medium">Cor: {color}</h5>
                      {previews.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {previews.length} arquivo(s)
                        </span>
                      )}
                    </div>

                    <label
                      htmlFor={inputId}
                      className="inline-flex items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Selecionar fotos
                    </label>
                    <input
                      id={inputId}
                      type="file"
                      name={inputId}
                      accept="image/*,video/*"
                      multiple
                      onChange={(e) => handleColorFilesChange(color, e)}
                      className="hidden"
                    />

                    {error && (
                      <div className="mt-2 text-sm text-red-600">{error}</div>
                    )}

                    {previews.length > 0 && (
                      <div className="mt-3 grid grid-cols-5 gap-2">
                        {previews.map((src, i) =>
                          src.startsWith('data:video') ? (
                            <video
                              key={i}
                              src={src}
                              className="h-20 w-full rounded-lg object-cover"
                              controls
                            />
                          ) : (
                            <img
                              key={i}
                              src={src || '/placeholder.svg'}
                              alt={`${color}-${i}`}
                              className="h-20 w-full rounded-lg object-cover"
                            />
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {variants.length > 0 && (
          <div>
            <h4 className="text-md font-medium mb-2">Variações Geradas</h4>
            <div className="flex justify-end gap-2 mb-4">
              <button
                type="button"
                onClick={() => bulkUpdate('price')}
                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
              >
                Preço em massa
              </button>
              <button
                type="button"
                onClick={() => bulkUpdate('stock')}
                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
              >
                Estoque em massa
              </button>
              <button
                type="button"
                onClick={clearTable}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
              >
                Limpar grade
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    {headers.map((header) => (
                      <th
                        key={header}
                        className="px-4 py-2 text-left border-b font-medium"
                      >
                        {header}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-left border-b font-medium">
                      Preço (R$)
                    </th>
                    <th className="px-4 py-2 text-left border-b font-medium">
                      Promoção
                    </th>
                    <th className="px-4 py-2 text-left border-b font-medium">
                      Desconto (%)
                    </th>
                    <th className="px-4 py-2 text-left border-b font-medium">
                      Estoque
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((variant, index) => (
                    <tr key={index} className="hover:bg-gray-50 align-top">
                      {headers.map((header) => (
                        <td key={header} className="px-4 py-2 text-sm border-b">
                          {variant[header]}
                        </td>
                      ))}
                      <td className="px-4 py-2 border-b">
                        <input
                          maxLength={8}
                          type="number"
                          step="0.01"
                          min="0"
                          value={variant.price}
                          onChange={(e) =>
                            updateVariant(index, 'price', e.target.value)
                          }
                          className="w-full p-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 border-b">
                        <select
                          value={variant.promo}
                          onChange={(e) =>
                            updateVariant(index, 'promo', e.target.value)
                          }
                          className="w-full p-1 border rounded text-sm"
                        >
                          <option value="Não">Não</option>
                          <option value="Sim">Sim</option>
                        </select>
                      </td>
                      <td className="px-4 py-2 border-b">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={variant.discount}
                          onChange={(e) =>
                            updateVariant(index, 'discount', e.target.value)
                          }
                          className="w-full p-1 border rounded text-sm"
                          style={{
                            display: variant.promo === 'Sim' ? 'block' : 'none',
                          }}
                          placeholder="%"
                        />
                      </td>
                      <td className="px-4 py-2 border-b">
                        <input
                          maxLength={6}
                          type="number"
                          min="0"
                          step="1"
                          value={variant.stock}
                          onChange={(e) =>
                            updateVariant(index, 'stock', e.target.value)
                          }
                          className="w-full p-1 border rounded text-sm"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface CreateProductUIProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  state?: boolean;
  formNameFiles?: NameFiles;
  categoryOptions: Category[];
  selectedCategories: Category[];
  filesImage: File[];
  filesImageGrade: File[];
  previewFiles: string[];
  previewFilesGrade: string;
  previewGradesSizes: GradeSize[];
  inputTags: string[];
  grade: Grade[];
  shippingCost: string | null;
  isGrade: boolean;
  onSubmitRegister: (e: FormEvent) => Promise<void>;
  FileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeFormFileInput: (
    inputName: string
  ) => (file: File | undefined) => void;
  onDeleteFile: (value: number) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tagToRemove: string) => void;
  onSelectCategory: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onRemoveCategory: (id: string) => void;
  addQuantiValue: (e: FormEvent) => void;
  removeGrade: (index: number) => void;
  onShippingCalc: (
    altura: string,
    largura: string,
    profundidade: string,
    peso: string
  ) => void;
  addSize: (e: FormEvent) => void;
  onChangeFormFileInputGradeSize: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onGrade: (inputName: string) => (value: string) => void;
  onAdvancedGradeChange: (grades: any[]) => void;
}

const statusOptions = [
  { value: 'Ativo', label: 'Ativo' },
  { value: 'Inativo', label: 'Inativo' },
  { value: 'Estoque Zerado', label: 'Estoque Zerado' },
];
const promotionOptions = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
];

export const CreateProductUI: React.FC<CreateProductUIProps> = ({
  state,
  formData,
  formErrors,
  formNameFiles,
  onChangeFormInput,
  onSubmitRegister,
  onChangeFormFileInput,
  filesImage,
  categoryOptions,
  onDeleteFile,
  previewFiles,
  previewFilesGrade,
  previewGradesSizes,
  selectedCategories,
  FileUpload,
  inputTags,
  onAddTag,
  onRemoveTag,
  onSelectCategory,
  onRemoveCategory,
  addQuantiValue,
  removeGrade,
  isGrade,
  grade,
  shippingCost,
  onShippingCalc,
  addSize,
  filesImageGrade,
  onGrade,
  onChangeFormFileInputGradeSize,
  onAdvancedGradeChange,
}) => {
  const [advancedGrades, setAdvancedGrades] = useState<any[]>([]);

  const handleGradeChange = (grades: any[]) => {
    setAdvancedGrades(grades);
    onAdvancedGradeChange(grades);
    onChangeFormInput(FormRegisterProduct.grade)(JSON.stringify(grades));
  };

  return (
    <section className="flex flex-col md:flex-row items-center px-2 justify-between w-full bg-white rounded-lg shadow-sm mt-4">
      <form
        onSubmit={onSubmitRegister}
        className="flex-col items-center w-full space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Produto *
            <Tooltip text="Digite o nome completo do produto que será exibido aos clientes">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <InputText
            maxLength={50}
            name="name"
            placeholder="Digite o nome do produto..."
            value={formData[FormRegisterProduct.name]}
            onChange={(e) =>
              onChangeFormInput(FormRegisterProduct.name)(e.target.value)
            }
            error={formErrors.name && formErrors.name[0]}
            inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição *
            <Tooltip text="Descreva detalhadamente o produto, suas características e benefícios">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <TextArea
            maxLength={2000}
            name="description"
            placeholder="Descreva o produto..."
            value={formData[FormRegisterProduct.description]}
            onChange={(e) =>
              onChangeFormInput(FormRegisterProduct.description)(e.target.value)
            }
            error={formErrors.description && formErrors.description[0]}
            textAreaClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 grid-rows-2 md:grid-rows-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SKU *
              <Tooltip text="Código único de identificação do produto (Stock Keeping Unit)">
                <span className="ml-1 text-blue-500 cursor-help">?</span>
              </Tooltip>
            </label>
            <InputText
              maxLength={12}
              name="sku"
              placeholder="Digite o código do SKU..."
              value={formData[FormRegisterProduct.sku]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.sku)(e.target.value)
              }
              error={formErrors.sku && formErrors.sku[0]}
              inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status *
              <Tooltip text="Define se o produto estará disponível para venda">
                <span className="ml-1 text-blue-500 cursor-help">?</span>
              </Tooltip>
            </label>
            <Select
              name="status"
              value={formData[FormRegisterProduct.status]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.status)(e.target.value)
              }
              error={formErrors.status && formErrors.status[0]}
              options={statusOptions}
              selectClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Categorias *
            <Tooltip text="Selecione as categorias que melhor descrevem seu produto">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <CategorySelector
            categorys={categoryOptions}
            selectedCategories={selectedCategories}
            selectCategory={onSelectCategory}
            removeCategory={onRemoveCategory}
            error={
              selectedCategories.length === 0
                ? 'Selecione pelo menos uma Categoria'
                : null
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags *
            <Tooltip text="Palavras-chave que ajudam os clientes a encontrar seu produto">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <InputTags
            maxLength={10}
            tags={inputTags}
            addTag={onAddTag}
            removeTag={onRemoveTag}
            error={
              inputTags.length === 0 ? 'Adicione pelo menos uma tag.' : null
            }
            inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Venda *
            <Tooltip text="Defina se o seu produto será vendido nacionalmente ou apenas localmente">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>

          <select
            value={formData[FormRegisterProduct.saleType] || ''}
            onChange={(e) =>
              onChangeFormInput(FormRegisterProduct.saleType)(e.target.value)
            }
            className="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
          >
            <option value="">Selecione</option>
            <option value="NACIONAL">
              Nacional — seu produto poderá ser enviado para qualquer endereço
            </option>
            <option value="LOCAL">
              Local — seu produto poderá ser enviado apenas para endereços da
              mesma cidade da sua loja
            </option>
          </select>

          {formErrors.saleType && (
            <p className="text-red-500 text-sm">{formErrors.saleType[0]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Marca *
            <Tooltip text="Nome da marca ou fabricante do produto">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <InputText
            name="brand"
            type="text"
            placeholder="Digite a marca do produto..."
            value={formData[FormRegisterProduct.brandName]}
            onChange={(e) =>
              onChangeFormInput(FormRegisterProduct.brandName)(e.target.value)
            }
            maxLength={20}
            error={formErrors.brandName && formErrors.brandName[0]}
            inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dimensões *
            <Tooltip text="Medidas do produto para cálculo de frete">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-4 grid-rows-4 md:grid-rows-1 gap-4">
            <InputText
              name="dimensions.width"
              type="text"
              label="Largura (cm)"
              placeholder="Digite a largura..."
              value={formData[FormRegisterProduct.dimensionsWidth]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.dimensionsWidth)(
                  clamp(e.target.value, 6)
                )
              }
              onBlur={() =>
                onShippingCalc(
                  formData[FormRegisterProduct.dimensionsHeight],
                  formData[FormRegisterProduct.dimensionsWidth],
                  formData[FormRegisterProduct.dimensionsDepth],
                  formData[FormRegisterProduct.dimensionsWeight]
                )
              }
              error={
                formErrors.dimensionsWidth && formErrors.dimensionsWidth[0]
              }
              inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
            />

            <InputText
              name="dimensions.height"
              type="text"
              label="Altura (cm)"
              inputMode="numeric"
              pattern="\d*"
              value={formData[FormRegisterProduct.dimensionsHeight]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.dimensionsHeight)(
                  clamp(e.target.value, 6)
                )
              }
              maxLength={4}
              error={formErrors.dimensionsHeight?.[0]}
              inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
              placeholder="Digite a altura..."
            />

            <InputText
              name="dimensions.depth"
              label="Profundidade (cm)"
              type="text"
              inputMode="numeric"
              placeholder="Digite a profundidade..."
              value={formData[FormRegisterProduct.dimensionsDepth]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.dimensionsDepth)(
                  clamp(e.target.value, 6)
                )
              }
              onBlur={() =>
                onShippingCalc(
                  formData[FormRegisterProduct.dimensionsHeight],
                  formData[FormRegisterProduct.dimensionsWidth],
                  formData[FormRegisterProduct.dimensionsDepth],
                  formData[FormRegisterProduct.dimensionsWeight]
                )
              }
              error={
                formErrors.dimensionsDepth && formErrors.dimensionsDepth[0]
              }
              inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
            />

            <InputText
              name="dimensions.weight"
              label="Peso (kg)"
              type="text"
              placeholder="Digite o peso..."
              value={formData[FormRegisterProduct.dimensionsWeight]}
              onChange={(e) =>
                onChangeFormInput(FormRegisterProduct.dimensionsWeight)(
                  clamp(e.target.value, 6)
                )
              }
              onBlur={() =>
                onShippingCalc(
                  formData[FormRegisterProduct.dimensionsHeight],
                  formData[FormRegisterProduct.dimensionsWidth],
                  formData[FormRegisterProduct.dimensionsDepth],
                  formData[FormRegisterProduct.dimensionsWeight]
                )
              }
              error={
                formErrors.dimensionsWeight && formErrors.dimensionsWeight[0]
              }
              inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
            />
          </div>
        </div>

        <h4 className="text-sm w-full text-gray-500">
          As dimensões máximas são de 100 cm em cada lado (altura, largura e
          comprimento), e a soma dos três lados não pode ultrapassar 200 cm. As
          dimensões mínimas são de 15 cm x 10 cm x 1 cm. O peso máximo é de 30
          kg.
        </h4>

        <div className="flex gap-2 flex-col min-w-full">
          {shippingCost && (
            <div className="flex w-full flex-col border rounded-lg p-2 shadow-lg">
              <h4 className="text-sm text-gray-500">
                A taxa de frete acima é um valor padrão. O valor exato do custo
                do frete será calculado no momento da compra baseando-se no
                endereço do comprador e do vendedor. Para promoções ativas de
                frete grátis, esta taxa não será considerada.
              </h4>
              <h4 className="text-sm font-semibold flex gap-1 items-center w-full justify-end">
                Frete Base: <span className="text-red-500">{shippingCost}</span>
              </h4>
            </div>
          )}
          <div className="flex gap-2 w-full items-center">
            <div className="w-full flex gap-2">
              <Checkbox
                name="shipping"
                value="true"
                checked={formData[FormRegisterProduct.freeShipping] === 'true'}
                label="Frete Grátis"
                error={formErrors.freeShipping && formErrors.freeShipping[0]}
                onChange={(value) => {
                  const newValue = value === 'true' ? 'true' : 'false';
                  onChangeFormInput(FormRegisterProduct.freeShipping)(newValue);
                }}
                checkboxClassName="w-[1rem] h-[1rem] border-red-400 border-[.1rem] accent-red-500 cursor-pointer"
                labelClassName="text-gray-500 text-md font-normal cursor-pointer"
              />
              <Tooltip text="Marque se o produto terá frete gratuito">
                <span className="ml-1 text-blue-500 cursor-help">?</span>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="flex gap-2 w-full items-center">
          <div className="w-full flex gap-2">
            <Checkbox
              name="isGrade"
              value="Sim"
              checked={formData[FormRegisterProduct.isGrade] === 'true'}
              label="Produto terá grade?"
              error={formErrors.isGrade && formErrors.isGrade[0]}
              onChange={(value) => {
                const newValue = value === 'true' ? 'true' : 'false';
                onChangeFormInput(FormRegisterProduct.isGrade)(newValue);
                onGrade(FormRegisterProduct.isGrade)(newValue);
              }}
              checkboxClassName="w-[1rem] h-[1rem] border-red-400 border-[.1rem]"
              labelClassName="text-gray-500 text-md font-"
            />
            <Tooltip text="Marque se o produto terá variações como cor, tamanho, etc.">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </div>
        </div>

        {!isGrade && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estoque *
                <Tooltip text="Quantidade disponível em estoque">
                  <span className="ml-1 text-blue-500 cursor-help">?</span>
                </Tooltip>
              </label>
              <InputText
                name="stock"
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={formData[FormRegisterProduct.stock]}
                onChange={(e) =>
                  onChangeFormInput(FormRegisterProduct.stock)(
                    clamp(onlyDigits(e.target.value), 6)
                  )
                }
                maxLength={6}
                error={formErrors.stock?.[0]}
                inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
                placeholder="Digite a quantidade em estoque"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor unitário *
                <Tooltip text="Preço de venda do produto em reais">
                  <span className="ml-1 text-blue-500 cursor-help">?</span>
                </Tooltip>
              </label>
              <InputText
                inputMode="decimal"
                name="price"
                type="text"
                value={formData[FormInputGradeSize.price]}
                onChange={(e) =>
                  onChangeFormInput(FormInputGradeSize.price)(
                    clamp(e.target.value, 8)
                  )
                }
                error={formErrors.price?.[0]}
                inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
                placeholder="Digite o preço"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promoção *
                <Tooltip text="Defina se o produto terá preço promocional">
                  <span className="ml-1 text-blue-500 cursor-help">?</span>
                </Tooltip>
              </label>
              <Select
                name="promotion"
                value={formData[FormRegisterProduct.promotion]}
                onChange={(e) =>
                  onChangeFormInput(FormRegisterProduct.promotion)(
                    e.target.value
                  )
                }
                error={formErrors.promotion && formErrors.promotion[0]}
                options={promotionOptions}
                selectClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
              />
            </div>

            {formData[FormRegisterProduct.promotion] === 'yes' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desconto (%) *
                  <Tooltip text="Percentual de desconto aplicado ao preço original">
                    <span className="ml-1 text-blue-500 cursor-help">?</span>
                  </Tooltip>
                </label>
                <InputText
                  name="discount"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={formData[FormInputGradeSize.discount]}
                  onChange={(e) =>
                    onChangeFormInput(FormInputGradeSize.discount)(
                      e.target.value
                    )
                  }
                  error={formErrors.discount?.[0]}
                  inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
                  placeholder="Digite o percentual de desconto"
                />
              </div>
            )}

            {formData.price && formData.discount && (
              <div className="text-sm mt-2 bg-gray-100 p-2 rounded-lg">
                {(() => {
                  const price = Number.parseFloat(formData.price);
                  const discount = Number.parseFloat(formData.discount);
                  const discountedPrice = price - (price * discount) / 100;
                  const fee = discountedPrice * 0.1 + 3;
                  const net = discountedPrice - fee;
                  const isNegative = net < 0;

                  return (
                    <>
                      <p>
                        💰 Preço com desconto:{' '}
                        <strong>{discountedPrice.toFixed(2)}</strong>
                      </p>
                      <p>
                        📉 Taxas (10% + R$3,00):{' '}
                        <strong>{fee.toFixed(2)}</strong>
                      </p>
                      <p>
                        📦 Valor líquido recebido:
                        <strong
                          className={
                            isNegative ? 'text-red-600' : 'text-green-600'
                          }
                        >
                          R$ {net.toFixed(2)}
                        </strong>
                      </p>
                      {isNegative && (
                        <p className="text-red-600 font-semibold">
                          ⚠️ Valor líquido negativo. Ajuste o preço ou o
                          desconto.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}

        {formErrors.isGrade && (
          <p className="text-red-500 text-sm">{formErrors.isGrade[0]}</p>
        )}

        {isGrade ? (
          <AdvancedGradeForm onGradeChange={handleGradeChange} />
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Garantia *
            <Tooltip text="Período de garantia oferecido pelo produto">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <InputText
            maxLength={30}
            name="warranty"
            placeholder="Digite ..."
            value={formData[FormRegisterProduct.warranty]}
            onChange={(e) =>
              onChangeFormInput(FormRegisterProduct.warranty)(e.target.value)
            }
            error={formErrors.warranty && formErrors.warranty[0]}
            inputClassName="rounded-lg p-2 w-full bg-[#fafafa] shadow-inner"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Upload de até{' '}
            <span className="text-red-500 font-semibold">
              4 imagens e 1 vídeo ou 5 imagens
            </span>
            <br />
            tamanho recomendado{' '}
            <span className="text-red-500 font-semibold">
              Min 800px X 800px
            </span>{' '}
            ou{' '}
            <span className="text-red-500 font-semibold">
              Max 1600px X 1600px
            </span>
            <Tooltip text="Adicione imagens de alta qualidade do produto para atrair mais clientes">
              <span className="ml-1 text-blue-500 cursor-help">?</span>
            </Tooltip>
          </label>
          <InputFile
            name="mediaFiles"
            label="Imagens ou vídeo"
            multiple
            onChange={FileUpload}
            error={filesImage.length > 5 ? 'Máximo de 5 arquivos' : undefined}
          />

          <div className="flex space-x-2 mt-2">
            {previewFiles.map((preview, index) => (
              <div key={index} className="relative">
                <img
                  src={preview || '/placeholder.svg'}
                  alt={`File ${index}`}
                  className="w-24 h-24 object-cover rounded"
                />
                <button
                  type="button"
                  className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full"
                  onClick={() => onDeleteFile(index)}
                >
                  X
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full flex justify-center">
          <Button
            type="submit"
            title="Cadastrar Produto"
            buttonStyle="red"
            size="xlg"
            className="w-[80%]"
            disabled={
              formData.name === '' ||
              formData.description === '' ||
              formData.status === '' ||
              selectedCategories.length === 0
            }
          />
        </div>
      </form>
    </section>
  );
};
